import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { useAuthContext } from "@kalyzee/kast-web-sdk";
import { MeetingAccessDenialReason, MeetingAccessibilityOutputDTO, MeetingPrivacy, ROLE } from "@kalyzee/kast-web-sdk/http";
import { audioContextIsReadyToUse, Input, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { ElapsedTimerPill } from "../../../../components/utils/ElapsedTimerPill";
import { LoadingView } from "../../../../components/utils/LoadingView";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { markSessionEphemeral } from "../../../../helpers/session";
import Storage from "../../../../helpers/storage";
import { toastError } from "../../../../helpers/toast";
import { useFeatures } from "../../../../hooks/features";
import { useMeetingAccessibility, useMeetingSources } from "../../../../hooks/meeting";
import { useSettings } from "../../../../hooks/settings";
import authStyles from "../../../public/login/auth.module.css";
import styles from "./lobby.module.css";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";

const PRIVACY_LABELS: Record<MeetingPrivacy, string> = {
  [MeetingPrivacy.PUBLIC]: "Public",
  [MeetingPrivacy.PRIVATE]: "Privé",
  [MeetingPrivacy.CLOSED]: "Restraint",
};

const REASON_MESSAGES: Record<MeetingAccessDenialReason, string> = {
  [MeetingAccessDenialReason.NOT_AUTHENTICATED]: "Connectez-vous pour accéder à ce meeting.",
  [MeetingAccessDenialReason.DIFFERENT_ORGANIZATION]: "Ce meeting appartient à une autre organisation.",
  [MeetingAccessDenialReason.NOT_ACCESSIBLE]: "Ce meeting ne vous est pas accessible.",
  // Never shown from here (see `canAccess` below), but the map must be total.
  [MeetingAccessDenialReason.SESSION_LIMIT_REACHED]: "Ce meeting est complet.",
};

/**
 * The lobby is public (registered in both route trees) and driven by a single
 * request: the meeting accessibility endpoint. Hero (chips + name) on the
 * left, camera selection on the right when relevant, actions centered below —
 * everything floats on the animated background, no boxes.
 */
export const MeetingLobbyPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const authCtx = useAuthContext();
  const sdkCtx = useSdkContext();
  const features = useFeatures();
  const [settings] = useSettings();
  const user = useAppStoreSelector(state => state.user);
  const sources = useMeetingSources();

  const connected = authCtx.status === "in";
  const isGuestRole = connected && user?.role === ROLE.GUEST;
  const disableMeetingList = settings?.ui?.disableMeetingList === true;

  const id = pageContext?.params?.id ?? "";
  const provider = pageContext?.searchParams.get(getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER)) ?? undefined;

  const accessibility = useMeetingAccessibility(id, { provider });
  // A session-limit refusal (JOIN rule) is dynamic — a seat can free up at any
  // time — and the lobby doesn't poll: don't treat it as a denial here. The
  // join attempt remains the authority (the meeting page shows the refusal).
  const canAccess = accessibility?.canAccess === true
    || accessibility?.reason === MeetingAccessDenialReason.SESSION_LIMIT_REACHED;

  // Once the accessibility endpoint has resolved a non-canonical id (shortId
  // or provider-scoped external id), rewrite the URL in place with the
  // meeting's real id and without the consumed `provider` query (`goTo` only
  // carries sticky queries).
  useEffect(() => {
    if (!accessibility?._id) return;
    if (accessibility._id === id && !provider) return;
    pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id: accessibility._id }, { replace: true });
  }, [id, provider, accessibility?._id, pageContextRef]);

  // I have visible sources
  const showSources = canAccess && sources.some(s => s.visible);

  // Shortcut to the replays.
  const showReplays = canAccess && (accessibility?.vods?.length ?? 0) > 0;

  // The public tree matches /lobby/:id, so an explicit logout can land here
  // without passing through PublicRedirectionRoute — consume its flag so it
  // doesn't wrongly suppress the next login-redirect capture.
  useEffect(() => {
    if (!connected) sessionStorage.removeItem("explicit_logout");
  }, [connected]);

  // ---- Guest access (logged-out callers on accessible meetings) ----

  const [guestName, setGuestName] = useState("");
  const [guestLoading, setGuestLoading] = useState(false);

  const storedGuest = useMemo(() => {
    if (connected || !features.rememberGuests) return undefined;
    if (!accessibility?._id) return undefined;
    return Storage.getGuestCredentialsForMeeting(accessibility._id);
  }, [connected, features.rememberGuests, accessibility?._id]);

  // One step: media is configured right here, so joining as a guest goes
  // straight into the meeting (tokens set, then navigate — same render).
  const enterMeetingAsGuest = useCallback((tokens: { token: string; refreshToken?: string }) => {
    const meetingId = accessibility?._id;
    // Guest sessions are ephemeral: their tokens stay in memory (a reload ends
    // the session) — the per-meeting stored credentials are their only durable
    // trace.
    markSessionEphemeral();
    authCtx.setTokens(tokens);
    if (meetingId) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: meetingId });
    }
  }, [authCtx, accessibility?._id]);

  const loginAsGuest = useCallback(async () => {
    const username = guestName.trim();
    if (!username || guestLoading) return;
    setGuestLoading(true);
    try {
      // The click is the user gesture — ensure audio context is unlocked
      await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
      const { data } = await sdkCtx.client
        .getHttpClient()
        .user.loginGuest({ username }, { include: "user" });
      if (!data?.token || !data?.user?._id) {
        toastError("Impossible de rejoindre en tant qu'invité");
        return;
      }
      // Persist before setTokens: flipping the tokens swaps the route tree and
      // remounts this page immediately.
      if (features.rememberGuests && accessibility?._id) {
        Storage.setGuestCredentialsForMeeting(accessibility._id, {
          id: data.user._id,
          username,
          token: data.token,
          refreshToken: data.refreshToken,
        });
      }
      enterMeetingAsGuest({ token: data.token, refreshToken: data.refreshToken });
    } catch (err) {
      console.error("[GUEST] login failed: ", err);
      toastError("Impossible de rejoindre en tant qu'invité");
    } finally {
      setGuestLoading(false);
    }
  }, [guestName, guestLoading, sdkCtx.client, features.rememberGuests, accessibility?._id, enterMeetingAsGuest]);

  // Guest tokens never expire — a stored identity can be resumed as-is.
  const resumeGuest = useCallback(async () => {
    if (!storedGuest) return;
    await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
    enterMeetingAsGuest({ token: storedGuest.token, refreshToken: storedGuest.refreshToken });
  }, [storedGuest, enterMeetingAsGuest]);

  // ---- Navigation ----

  const goToLogin = useCallback(() => {
    const search = provider ? `?${getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER)}=${provider}` : "";
    // The provider belongs inside `redirect`: the lobby needs it to resolve the
    // external meeting once we come back.
    pageContextRef.current?.goTo(PageMasterMode.LOGIN, undefined, { queries: { [getGlobalQueryLabel(NavigateGlobalQuery.REDIRECT)]: `lobby/${id}${search}` } });
  }, [id, provider]);

  // Guests can't browse meetings — offer a real login instead. Navigate first,
  // then drop the session: the public tree then renders /login with the
  // redirect intact. No `explicit_logout` — we want the round-trip back here.
  const onLoginWithAccount = useCallback(() => {
    pageContextRef.current?.goTo(PageMasterMode.LOGIN, undefined, { queries: { [getGlobalQueryLabel(NavigateGlobalQuery.REDIRECT)]: `lobby/${id}` } });
    authCtx.logout();
  }, [authCtx, id]);

  // Top bar back arrow. From the lobby there is no meeting to leave, so it goes
  // to whatever is "before": the meeting list for a real account, the login
  // page for logged-out visitors and guests (who have no list).
  const onBack = useCallback(() => {
    if (connected && !isGuestRole) {
      pageContextRef.current?.goTo(PageMasterMode.LIST);
    } else {
      pageContextRef.current?.goTo(PageMasterMode.LOGIN);
    }
  }, [connected, isGuestRole]);

  const onJoin = useCallback(async () => {
    // The click is the user gesture — ensure audio context is unlocked
    await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
    const meetingId = accessibility?._id;
    if (!meetingId) return;
    pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: meetingId });
  }, [accessibility?._id]);

  const onGoToVod = useCallback(() => {
    const meetingId = accessibility?._id;
    if (!meetingId) return;
    // `_id` is the resolved MongoDB id, so /player needs no `provider`.
    pageContextRef.current?.goTo(PageMasterMode.PLAYER, { id: meetingId });
  }, [accessibility?._id]);

  // ---- Render ----

  // A denied meeting must always carry a motive, but the backend sometimes
  // omits `reason` (or returns one we don't know). Fall back on what we do
  // know: an anonymous caller is invited to authenticate, a connected one is
  // simply not allowed (meeting not accessible).
  const deniedSubtitle = (a: MeetingAccessibilityOutputDTO) => {
    const reason = a.reason && REASON_MESSAGES[a.reason]
      ? a.reason
      : connected
        ? MeetingAccessDenialReason.NOT_ACCESSIBLE
        : MeetingAccessDenialReason.NOT_AUTHENTICATED;
    return REASON_MESSAGES[reason];
  };

  const renderHero = (a: MeetingAccessibilityOutputDTO | null) => {
    const subtitle = a === null
      ? "Ce meeting n'existe pas ou le lien est invalide."
      : !canAccess
        ? deniedSubtitle(a)
        : !connected
          ? "Rejoignez directement en tant qu'invité, ou connectez-vous à votre compte."
          : showSources
            ? "Configurez vos sources et rejoignez le meeting."
            : undefined;

    return (
      <div className={`${styles.hero} ${showSources ? "" : styles.heroCentered}`}>
        {a && (
          <div className={styles.chips}>
            <span className={styles.chip}>
              <span
                className={`${styles.privacyDot} ${a.privacy === MeetingPrivacy.PUBLIC ? styles.dotPublic : styles.dotRestricted}`}
              />
              {PRIVACY_LABELS[a.privacy]}
            </span>
            {a.subOrganization?.name && <span className={styles.chip}>{a.subOrganization.name}</span>}
            {a.room?.name && <span className={styles.chip}>{a.room.name}</span>}
            {canAccess && a.startedAt && (
              <ElapsedTimerPill since={new Date(a.startedAt)} className={styles.timerChip} />
            )}
          </div>
        )}
        <h1 className={styles.heroTitle}>{a === null ? "Meeting introuvable" : a.name ?? "Réunion"}</h1>
        {subtitle && <p className={styles.heroSubtitle}>{subtitle}</p>}
      </div>
    );
  };

  const renderGuestActions = () => (
    <>
      <form
        className={styles.guestForm}
        onSubmit={(e) => {
          e.preventDefault();
          loginAsGuest();
        }}
      >
        <Input
          className={authStyles.glassInput}
          value={guestName}
          setValue={setGuestName}
          disabled={guestLoading}
          nativeProps={{ placeholder: "Votre nom" }}
        />
        <button
          type="submit"
          className={`${authStyles.button} ${authStyles.buttonGreen}`}
          disabled={guestLoading || !guestName.trim()}
        >
          Rejoindre en tant qu'invité
        </button>
      </form>

      {storedGuest?.username && (
        <button type="button" className={authStyles.link} onClick={resumeGuest}>
          {`Reprendre en tant que ${storedGuest.username}`}
        </button>
      )}

      <div className={styles.orDivider}>ou</div>

      <button type="button" className={authStyles.button} onClick={goToLogin}>
        Se connecter
      </button>
    </>
  );

  const renderActions = () => {
    if (canAccess) {
      if (!connected) return renderGuestActions();
      return (
        <button type="button" className={authStyles.button} onClick={onJoin}>
          Rejoindre le meeting
        </button>
      );
    }
    // Refused or not found.
    if (!connected) {
      return (
        <button type="button" className={authStyles.button} onClick={goToLogin}>
          Se connecter
        </button>
      );
    }
    if (isGuestRole) {
      return (
        <>
          <p className={styles.hint}>
            Vous êtes connecté en tant qu'invité — connectez-vous avec un compte pour y accéder.
          </p>
          <button type="button" className={authStyles.button} onClick={onLoginWithAccount}>
            Se connecter avec un compte
          </button>
        </>
      );
    }
    return (
      <button type="button" className={authStyles.button} onClick={onBack}>
        Sortir du meeting
      </button>
    );
  };

  return (
    // In no-list mode the lobby offers no top bar way out: the /list "join a
    // meeting" screen is a safety net, not a destination.
    <MasterUI onBack={disableMeetingList ? undefined : onBack}>
      <AnimatedBackground contentClassName={styles.container}>
        {accessibility === undefined ? (
          <LoadingView message="Récupération du lobby" />
        ) : (
          <div className={styles.layout}>
            {showReplays && (
              <button type="button" className={styles.replayBtn} onClick={onGoToVod}>
                <ReactIcons.PlayScreens className={styles.replayIcon} />
                Voir les replays
              </button>
            )}
            <div className={styles.main}>
              {renderHero(accessibility)}
              {showSources && (
                <div className={styles.cameras}>
                  <MediaSourcePicker
                    sources={sources}
                    className={styles.mediaSelector}
                    // We can ignore gesture errors here because this component
                    // is only used to configure the sources: it will be mounted
                    // again when joining the meeting and there wont be gesture
                    // errors anymore
                    ignoreGestureError
                  />
                  <MediaGlobalControls sources={sources} className={styles.globalControls} />
                </div>
              )}
            </div>
            <div className={styles.bottom}>{renderActions()}</div>
          </div>
        )}
      </AnimatedBackground>
    </MasterUI>
  );
};
